import {openDb} from "../../../Services/BrowserDatabaseIDB";
import {ensureStoresExist} from "../../../Services/BrowserDatabaseFunctions";

export function addEventActivity(_event_id, _activity) {
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Events", "readwrite");
            const dataStore = tx.objectStore("Events");

            // Get the specific event by its id
            return dataStore.get(_event_id).then(event => {
                if (!event) {
                    console.error(`Event with ID ${_event_id} not found.`);
                    return false;
                }

                // Ensure activities is an array safely
                const activities = Array.isArray(event.activities)
                    ? event.activities.slice()
                    : [];

                // 🟢 FIXED: Fallback to matching by temp_id if permanent ID isn't linked yet!
                const idx = activities.findIndex(m => {
                    if (!m || !_activity) return false;

                    // Match Case A: Check permanent server database primary keys
                    if (m.id != null && _activity.id != null && String(m.id) === String(_activity.id)) {
                        return true;
                    }

                    // Match Case B: Check client-side transient creation tokens
                    if (m.temp_id != null && _activity.temp_id != null && String(m.temp_id) === String(_activity.temp_id)) {
                        return true;
                    }

                    return false;
                });

                if (idx >= 0) {
                    // Update existing activity; merge fields cleanly
                    const base = activities[idx];
                    const patch = _activity;
                    const merged = { ...base };
                    Object.keys(patch).forEach(k => {
                        if (typeof patch[k] !== "undefined") merged[k] = patch[k];
                    });
                    activities[idx] = merged;
                } else {
                    // Add new activity (Handles peer-created items perfectly)
                    activities.push(_activity);
                }

                // Write the mutated array back onto the parent event object context
                event.activities = activities;

                // Sanitize to avoid DataCloneError (functions, weird objects, etc.)
                const sanitizedEvent = JSON.parse(JSON.stringify(event));

                return dataStore.put(sanitizedEvent).then(() => {
                    console.log(
                        `Activity with ID ${_activity.id || 'TEMP'} in Event with ID ${_event_id} added/updated successfully.`
                    );
                    return true;
                });
            });
        })
        .catch(function (error) {
            console.error("An error occurred in addEventActivity:", error);
            throw error;
        });
}

export function updateEventActivity(_event_id, _activity) {
    console.log("updateEventActivity : ", _activity);

    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Events", "readwrite");
            const dataStore = tx.objectStore("Events");

            // Get the specific event by its id
            return dataStore.get(_event_id).then(event => {
                if (!event) {
                    console.error(`Event with ID ${_event_id} not found.`);
                    return false;
                }

                // ensure activities is an array
                const activities = Array.isArray(event.activities)
                    ? event.activities.slice()
                    : [];

                // prefer matching by id
                // 🟢 FIXED: Check permanent database IDs OR fallback to client-side temp_id tokens
                const idx = activities.findIndex(m => {
                    if (!m || !_activity) return false;

                    // Match Case A: Check primary server database keys
                    if (m.id != null && _activity.id != null && String(m.id) === String(_activity.id)) {
                        return true;
                    }

                    // Match Case B: Check client-side transient creation tokens
                    if (m.temp_id != null && _activity.temp_id != null && String(m.temp_id) === String(_activity.temp_id)) {
                        return true;
                    }

                    return false;
                });

                if (idx >= 0) {
                    const { merged, changed } = mergeChanged(activities[idx], _activity);
                    if (changed) activities[idx] = merged;
                } else {
                    // add new activity if not found
                    activities.push(_activity);
                }

                // write back
                event.activities = activities;

                const sanitizedEvent = JSON.parse(JSON.stringify(event));

                return dataStore.put(sanitizedEvent).then(() => {
                    console.log(
                        `Activity with ID ${_activity.id} in Event with ID ${_event_id} added/updated successfully.`
                    );
                    return true;
                });
            });
        })
        .catch(function (error) {
            console.error("An error occurred during updateEventActivity:", error);
            throw error;
        });
}
export function deleteEventActivity(_event_id, _activity_id) {
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Events", "readwrite"); // Use 'readwrite' mode for updates
            const dataStore = tx.objectStore("Events");

            // Get the specific event by its id
            return dataStore.get(_event_id).then(event => {
                if (!event) {
                    console.error(`Event with ID ${_event_id} not found.`);
                    return false;
                }

                const activities = Array.isArray(event.activities)
                    ? event.activities
                    : [];

                // Filter out the activity with the specified ID
                const updatedActivities = activities.filter(activity => {
                    if (!activity) return false;
                    return String(activity.id) !== String(_activity_id);
                });

                // Update the event with the modified activities array
                event.activities = updatedActivities;

                const sanitizedEvent = JSON.parse(JSON.stringify(event));

                // Put the updated event back into the object store
                return dataStore.put(sanitizedEvent).then(() => {
                    console.log(
                        `Activity with ID ${_activity_id} in Event with ID ${_event_id} deleted successfully.`
                    );
                    return true;
                });
            });
        })
        .catch(function (error) {
            console.error("An error occurred during deleteEventActivity:", error);
            throw error;
        });
}


const isPlainObject = (v) =>
    v !== null && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype;

function deepEqualJSON(a, b) {
    if (a === b) return true;
    if (a === null || b === null) return a === b;
    const ta = typeof a, tb = typeof b;
    if (ta !== tb) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) if (!deepEqualJSON(a[i], b[i])) return false;
        return true;
    }

    if (isPlainObject(a) && isPlainObject(b)) {
        const ak = Object.keys(a), bk = Object.keys(b);
        if (ak.length !== bk.length) return false;
        for (const k of ak) {
            if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
            if (!deepEqualJSON(a[k], b[k])) return false;
        }
        return true;
    }

    // primitives (number/string/boolean/undefined) or mismatched structures
    return false;
}

/**
 * Deep merge only for keys present in `patch`.
 * - Plain objects are merged recursively.
 * - Arrays are replaced wholesale.
 * - Primitives are overwritten.
 * Returns { merged, changed }.
 */
function mergeChanged(base = {}, patch = {}) {
    if (!isPlainObject(base) || !isPlainObject(patch)) {
        // Fallback: replace when not both plain objects
        return {
            merged: deepEqualJSON(base, patch) ? base : patch,
            changed: !deepEqualJSON(base, patch),
        };
    }

    let changed = false;
    const out = { ...base };

    for (const [k, pv] of Object.entries(patch)) {
        const bv = base[k];

        if (Array.isArray(pv)) {
            if (!deepEqualJSON(bv, pv)) {
                out[k] = pv.slice(); // replace array
                changed = true;
            }
            continue;
        }

        if (isPlainObject(pv) && isPlainObject(bv)) {
            const inner = mergeChanged(bv, pv);
            if (inner.changed) {
                out[k] = inner.merged;
                changed = true;
            }
            continue;
        }

        // primitives or differing shapes
        if (!deepEqualJSON(bv, pv)) {
            out[k] = pv;
            changed = true;
        }
    }

    return { merged: out, changed };
}